import { env } from '@/config/env';
import { appendPreviewId, isPreviewMode } from '@/utils/previewUtils';
import type {
  EducateBootstrap,
  EducateStoryline,
  EducateInteraction,
  EducateProgress
} from '@/types/educate';

export class EducateApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'EducateApiError';
  }
}

interface Envelope<T> {
  success: boolean;
  data: T;
  error?: string;
  type?: 'redirect';
  url?: string;
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(appendPreviewId(url), {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      ...init.headers
    }
  });

  if (!response.ok) {
    const body = await response.text();
    let message = body || response.statusText;
    try {
      const parsed = JSON.parse(body) as { error?: string; message?: string };
      message = parsed.error ?? parsed.message ?? message;
    } catch {}
    throw new EducateApiError(message, response.status);
  }

  const payload = (await response.json()) as Envelope<T>;

  if (payload.type === 'redirect' && payload.url) {
    window.location.href = payload.url;
    return new Promise<T>(() => {});
  }

  if (payload.success === false) {
    throw new EducateApiError(payload.error ?? 'Request failed', response.status);
  }

  return payload.data;
}

export interface SaveProgressInput {
  currentIndex: number;
  currentUnitId?: string | null;
  totalUnits: number;
  completedUnits: string[];
  interactions?: EducateInteraction[];
  timeSpentSeconds?: number;
}

const toBody = ({
  currentIndex,
  currentUnitId,
  totalUnits,
  completedUnits,
  interactions = [],
  timeSpentSeconds
}: SaveProgressInput) =>
  JSON.stringify({
    progress: { currentIndex, currentUnitId, totalUnits, completedUnits },
    interactions,
    timeSpentSeconds
  });

let bootstrapInFlight: Promise<EducateBootstrap> | null = null;

export const educateApi = {
  bootstrap: () => {
    if (!bootstrapInFlight) {
      bootstrapInFlight = request<EducateBootstrap>(env.educate.bootstrapUrl).finally(() => {
        bootstrapInFlight = null;
      });
    }
    return bootstrapInFlight;
  },

  getStoryline: () => request<EducateStoryline>(env.educate.storylineUrl),

  getProgress: () => request<EducateProgress>(env.educate.progressUrl),

  saveProgress: (input: SaveProgressInput) =>
    request<EducateProgress>(env.educate.progressUrl, {
      method: 'POST',
      body: toBody(input)
    }),

  complete: (input: SaveProgressInput) =>
    request<EducateProgress>(env.educate.completeUrl, {
      method: 'POST',
      body: toBody(input)
    }),

  reset: () =>
    request<EducateProgress>(env.educate.resetUrl, {
      method: 'POST',
      body: JSON.stringify({})
    }),

  markStarted: () => {
    if (isPreviewMode()) return;
    void fetch(appendPreviewId(env.educate.markStartedUrl), {
      credentials: 'include'
    }).catch(() => undefined);
  },

  markCompleted: () => {
    if (isPreviewMode()) return;
    void fetch(appendPreviewId(env.educate.markCompletedUrl), {
      credentials: 'include'
    }).catch(() => undefined);
  }
};
